import { StrictMode, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { Provider as ReduxProvider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter } from "react-router-dom";
import { IntlProvider } from "react-intl";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { store, persistor, useAppSelector } from "@/store/redux";
import { selectLocale } from "@/store/redux/slices/localeSlice";
import { selectThemeMode } from "@/store/redux/slices/themeSlice";
import { getMessages } from "@/utils/locales";
import { QueryProvider } from "@/providers/QueryProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import App from "./App";
import "./index.css";
import { createAppTheme } from "./themes";

// Wrapper component to access Redux state for locale and theme
export function AppWrapper() {
  const locale = useAppSelector(selectLocale);
  const themeMode = useAppSelector(selectThemeMode);
  const messages = getMessages(locale);

  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  return (
    <IntlProvider locale={locale} messages={messages}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <QueryProvider>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </QueryProvider>
        </ThemeProvider>
      </BrowserRouter>
    </IntlProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ReduxProvider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AppWrapper />
      </PersistGate>
    </ReduxProvider>
  </StrictMode>
);
